import {useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Alert, Group, Modal, Stack, Table, Text, TextInput} from '@mantine/core';
import {AiOutlineUpload, AiOutlineWarning} from 'react-icons/ai';
import {FlexiButton, FlexiCancelButton} from '@flexinet/ui-components';

import * as PlaybooksService from '../services/PlaybooksService';
import {summarizeDefinition, type DefinitionSummary} from '../utils/playbookDefinition';
import {notifyError, notifySuccess} from '../../../components/PlatformNotification';

// FNP-1011: create a playbook from an exported definition file.
//
// ⚠️ THE PREVIEW IS NOT DECORATION.
//
// Import creates a workflow that can drive a Step Functions state machine, and
// since FNP-998 that includes a teardown which deletes buckets, CloudFront
// distributions, DNS records, pipelines and repos. The moment between choosing
// a file and writing it is the ONLY point a human can notice they picked the
// wrong one — so the counts are shown and a second click is required.
//
// This is a human action in a specific environment's console. It is not
// seeding, and it must not become seeding: nothing here runs unattended.

export const ImportRecordButton = () => {
    const navigate = useNavigate();
    const fileInput = useRef<HTMLInputElement>(null);

    const [opened, setOpened] = useState(false);
    const [busy, setBusy] = useState(false);
    const [definition, setDefinition] = useState<string | null>(null);
    const [summary, setSummary] = useState<DefinitionSummary | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    const [nameOverride, setNameOverride] = useState('');

    const reset = () => {
        setDefinition(null);
        setSummary(null);
        setProblem(null);
        setNameOverride('');
        if (fileInput.current) {
            fileInput.current.value = '';
        }
    };

    const handleFile = (file: File) => {
        // FileReader rather than a Dropzone dependency: one JSON file needs no
        // library, and @mantine/dropzone is not currently a dependency of this
        // app.
        const reader = new FileReader();

        reader.onload = () => {
            const raw = String(reader.result ?? '');
            setDefinition(raw);
            try {
                setSummary(summarizeDefinition(raw));
                setProblem(null);
            } catch (err) {
                // Shown inline rather than as a toast: the admin needs to read
                // it while looking at the file they chose.
                setSummary(null);
                setProblem((err as Error).message);
            }
        };

        reader.onerror = () => {
            setSummary(null);
            setProblem('That file could not be read.');
        };

        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (!definition || !summary) {
            return;
        }

        setBusy(true);
        try {
            const result = await PlaybooksService.importRecord(definition, nameOverride);

            if (!result) {
                notifyError(new Error('Import returned no result.'));
                return;
            }

            // The counts come from the SERVER, not the file — what was actually
            // created, rather than what the file claimed.
            notifySuccess(
                `Imported '${result.name}' with ${result.phaseCount} phases and `
                + `${result.stepCount} steps.`,
            );

            setOpened(false);
            reset();

            // Land on the new playbook so the admin can verify it and set
            // anything the import deliberately left unset.
            navigate(`/playbooks/${result.id}`);
        } catch (err) {
            // Surfaced, not swallowed: the backend's refusals (duplicate name,
            // a carried ARN, an unsupported version) are written to be read by
            // a person.
            notifyError(err as Error);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <FlexiButton
                key={'import'}
                variant={'outline'}
                leftSection={<AiOutlineUpload/>}
                onClick={() => setOpened(true)}
            >
                Import
            </FlexiButton>

            <Modal
                opened={opened}
                onClose={() => {
                    setOpened(false);
                    reset();
                }}
                title={'Import a playbook'}
                size={'lg'}
            >
                <Stack>
                    <Text size={'sm'}>
                        Choose a playbook definition exported from a Flexi Network console.
                        The playbook, its phases and its steps are created together — if
                        anything fails, nothing is created.
                    </Text>

                    <input
                        ref={fileInput}
                        type={'file'}
                        accept={'application/json,.json'}
                        onChange={(event) => {
                            const file = event.currentTarget.files?.[0];
                            if (file) {
                                handleFile(file);
                            }
                        }}
                    />

                    {problem && (
                        <Alert
                            color={'red'}
                            icon={<AiOutlineWarning/>}
                            title={'That file cannot be imported'}
                        >
                            {problem}
                        </Alert>
                    )}

                    {summary && (
                        <>
                            <Alert color={'blue'} title={'About to create'}>
                                <Table withRowBorders={false}>
                                    <Table.Tbody>
                                        <Table.Tr>
                                            <Table.Td><Text fw={600}>Playbook</Text></Table.Td>
                                            <Table.Td>{nameOverride.trim() || summary.name}</Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Td><Text fw={600}>Phases</Text></Table.Td>
                                            <Table.Td>{summary.phaseCount}</Table.Td>
                                        </Table.Tr>
                                        <Table.Tr>
                                            <Table.Td><Text fw={600}>Steps</Text></Table.Td>
                                            <Table.Td>{summary.stepCount}</Table.Td>
                                        </Table.Tr>
                                    </Table.Tbody>
                                </Table>
                            </Alert>

                            <TextInput
                                label={'Import under a different name'}
                                description={
                                    'Optional. A playbook name must be unique in this '
                                    + 'environment, so use this to import a copy alongside '
                                    + 'an existing one.'
                                }
                                placeholder={summary.name}
                                value={nameOverride}
                                onChange={(event) => setNameOverride(event.currentTarget.value)}
                            />

                            <Text size={'xs'} c={'dimmed'}>
                                The state machine ARN is not part of a definition — it is
                                environment-specific and is bound when this environment is
                                deployed.
                            </Text>
                        </>
                    )}

                    <Group justify={'flex-end'}>
                        <FlexiCancelButton
                            onClick={() => {
                                setOpened(false);
                                reset();
                            }}
                        />
                        <FlexiButton
                            variant={'filled'}
                            disabled={!summary || busy}
                            loading={busy}
                            onClick={() => {
                                void handleImport();
                            }}
                        >
                            Import
                        </FlexiButton>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};
